import React, { useState, useEffect, useRef } from 'react';
import { 
    XIcon, GlobeIcon, UsersIcon, MailIcon, 
    ChevronRightIcon, SearchIcon, SendIcon
} from './Icons';
import { Community, UserData, MediaAttachment } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { stickerTextStyle } from './StickerText';
import { PostCreator } from './PostCreator';

interface ShareToGoodLyfeModalProps {
    isOpen: boolean;
    onClose: () => void;
    sharedData: {
        text?: string;
        url?: string;
        files?: { url: string; type: 'image' | 'video' | 'audio' }[];
    } | null;
    communities: Community[];
    usersData: Record<string, UserData>;
    currentUser: string | null;
    onShare: (params: {
        destination: 'profile' | 'community' | 'dm';
        communityId?: string;
        channelId?: string;
        recipientId?: string;
        text: string;
        media: MediaAttachment[];
    }) => void;
}

export const ShareToGoodLyfeModal = ({
    isOpen,
    onClose,
    sharedData,
    communities,
    usersData,
    currentUser,
    onShare
}: ShareToGoodLyfeModalProps) => {
    const { authUsersData } = useAuth();
    const activeUsersData = { ...usersData, ...authUsersData };
    const currentUserData = currentUser ? activeUsersData[currentUser] : null;
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(g) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, ${opacityVal})` : `rgba(26, 26, 46, ${opacityVal})`;
    };

    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    const [destination, setDestination] = useState<'profile' | 'community' | 'dm'>('profile');
    const [selectedCommunity, setSelectedCommunity] = useState<Community | null>(null);
    const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
    const [selectedRecipient, setSelectedRecipient] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    if (!isOpen || !sharedData) return null;

    const handlePostCreated = (postData: any) => {
        onShare({
            destination,
            communityId: selectedCommunity?._id,
            channelId: selectedChannel || undefined,
            recipientId: selectedRecipient || undefined,
            text: postData.content.text || '',
            media: postData.content.media || []
        });
        onClose();
    };

    const filteredCommunities = communities.filter(c => 
        c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredRecipients = Object.entries(activeUsersData)
        .filter(([username]) => username !== currentUser)
        .map(([username, data]) => ({ ...(data as any), username }))
        .filter(u => u.username.toLowerCase().includes(searchQuery.toLowerCase()));

    return (
        <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <div 
                className="rounded-[2.5rem] shadow-2xl max-w-xl w-full mx-4 backdrop-blur-md max-h-[90vh] flex flex-col border border-white/10 overflow-hidden"
                style={{ backgroundColor: modalBg, color: userTextColor }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-6 pb-4 flex items-center justify-between border-b border-white/5">
                    <h3 className="text-2xl font-black italic uppercase tracking-tighter" style={{ ...stickerTextStyle(0.18), color: userThemeColor }}>
                        Share to GoodLyfe
                    </h3>
                    <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-xl transition-colors" style={{ color: userTextColor }}>
                        <XIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-grow overflow-y-auto no-scrollbar p-6 space-y-6">
                    {/* Destination Selection */}
                    <div className="space-y-3">
                        <label className="text-xs font-black uppercase tracking-[0.2em] px-1 opacity-50" style={stickerTextStyle(0.14)}>
                            SELECT DESTINATION
                        </label>
                        <div className="flex p-1 bg-black/20 border border-white/5 rounded-2xl">
                            {(['profile', 'community', 'dm'] as const).map(dt => (
                                <button
                                    key={dt}
                                    onClick={() => {
                                        setDestination(dt);
                                        setSearchQuery('');
                                    }}
                                    className={`flex-grow py-2 rounded-xl text-xs font-bold transition-all ${
                                        destination === dt 
                                        ? 'text-white shadow-md' 
                                        : 'opacity-60 hover:opacity-100'
                                    }`}
                                    style={destination === dt ? { backgroundColor: userThemeColor, ...stickerTextStyle(0.14) } : stickerTextStyle(0.14)}
                                >
                                    {dt.toUpperCase()}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Destination Sub-options */}
                    <div className="space-y-4 animate-fade-in">
                        {destination === 'profile' && (
                            <div className="p-4 bg-black/20 border border-white/5 rounded-3xl flex items-center gap-4">
                                <div className="p-3 rounded-2xl" style={{ backgroundColor: `${userThemeColor}15` }}>
                                    <GlobeIcon className="w-6 h-6" style={{ color: userThemeColor }} />
                                </div>
                                <div>
                                    <p className="font-bold text-sm" style={stickerTextStyle(0.16)}>Your Profile Feed</p>
                                    <p className="text-xs opacity-60">Share this post to your main profile</p>
                                </div>
                            </div>
                        )}

                        {destination === 'community' && (
                            <div className="space-y-3">
                                {!selectedCommunity ? (
                                    <>
                                        <div className="relative">
                                            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" />
                                            <input
                                                type="text"
                                                value={searchQuery}
                                                onChange={e => setSearchQuery(e.target.value)}
                                                placeholder="Search communities..."
                                                className="w-full bg-black/30 focus:border-white/30 border border-white/5 rounded-2xl py-3 pl-10 pr-4 focus:outline-none text-sm placeholder-white/30"
                                                style={{ color: userTextColor }}
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 gap-2 max-h-[160px] overflow-y-auto pr-2 custom-scrollbar">
                                            {filteredCommunities.map(c => (
                                                <button
                                                    key={c._id}
                                                    onClick={() => {
                                                        setSelectedCommunity(c);
                                                        setSelectedChannel(c.channels?.[0]?.id || 'general');
                                                    }}
                                                    className="flex items-center gap-3 p-3 hover:bg-black/10 rounded-2xl transition-colors group text-left border border-transparent hover:border-white/5"
                                                >
                                                    <div className="w-10 h-10 bg-black/20 border border-white/5 rounded-xl overflow-hidden">
                                                        {c.avatar ? <img src={c.avatar} className="w-full h-full object-cover" alt="" /> : <UsersIcon className="w-full h-full p-2 opacity-50" />}
                                                    </div>
                                                    <div className="flex-grow">
                                                        <p className="font-bold text-sm" style={stickerTextStyle(0.16)}>{c.name}</p>
                                                        <p className="text-[10px] opacity-55 truncate">{c.description}</p>
                                                    </div>
                                                    <ChevronRightIcon className="w-4 h-4 opacity-30 group-hover:opacity-70" />
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <div className="p-3 bg-black/20 border border-white/5 rounded-3xl space-y-3">
                                        <div className="flex items-center gap-3 pb-3 border-b border-white/5">
                                            <div className="w-8 h-8 bg-black/20 border border-white/5 rounded-lg overflow-hidden">
                                                {selectedCommunity.avatar ? <img src={selectedCommunity.avatar} className="w-full h-full object-cover" alt="" /> : <UsersIcon className="w-full h-full p-1.5 opacity-50" />}
                                            </div>
                                            <span className="font-bold text-sm" style={stickerTextStyle(0.16)}>{selectedCommunity.name}</span>
                                            <button onClick={() => setSelectedCommunity(null)} className="ml-auto text-xs font-bold hover:underline" style={{ color: userThemeColor }}>Change</button>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {(selectedCommunity.channels || [{id: 'general', name: 'General'}]).map(ch => (
                                                <button
                                                    key={ch.id}
                                                    onClick={() => setSelectedChannel(ch.id)}
                                                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${
                                                        selectedChannel === ch.id 
                                                        ? 'border-transparent text-white' 
                                                        : 'bg-black/20 border-white/5 opacity-60 hover:opacity-100'
                                                    }`}
                                                    style={selectedChannel === ch.id ? { backgroundColor: userThemeColor } : undefined}
                                                >
                                                    # {ch.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {destination === 'dm' && (
                            <div className="space-y-3">
                                {!selectedRecipient ? (
                                    <>
                                        <div className="relative">
                                            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" />
                                            <input
                                                type="text"
                                                value={searchQuery}
                                                onChange={e => setSearchQuery(e.target.value)}
                                                placeholder="Search recipients..."
                                                className="w-full bg-black/30 focus:border-white/30 border border-white/5 rounded-2xl py-3 pl-10 pr-4 focus:outline-none text-sm placeholder-white/30"
                                                style={{ color: userTextColor }}
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 gap-2 max-h-[160px] overflow-y-auto pr-2 custom-scrollbar">
                                            {filteredRecipients.map(u => (
                                                <button
                                                    key={u.username}
                                                    onClick={() => setSelectedRecipient(u.username)}
                                                    className="flex items-center gap-3 p-3 hover:bg-black/10 rounded-2xl transition-colors group text-left border border-transparent hover:border-white/5"
                                                >
                                                    <img src={u.pfp} className="w-10 h-10 rounded-full object-cover border border-white/5" alt="" />
                                                    <div className="flex-grow">
                                                        <p className="font-bold text-sm" style={stickerTextStyle(0.16)}>{u.username}</p>
                                                        <p className="text-[10px] opacity-50">@{u.username.toLowerCase()}</p>
                                                    </div>
                                                    <ChevronRightIcon className="w-4 h-4 opacity-30 group-hover:opacity-70" />
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <div className="p-4 bg-black/20 border border-white/5 rounded-3xl flex items-center gap-4">
                                        <img src={activeUsersData[selectedRecipient]?.pfp} className="w-12 h-12 rounded-full object-cover border border-white/5" alt="" />
                                        <div>
                                            <p className="font-bold text-sm" style={stickerTextStyle(0.16)}>{selectedRecipient}</p>
                                            <button onClick={() => setSelectedRecipient(null)} className="text-xs font-bold hover:underline text-left block" style={{ color: userThemeColor }}>Change Recipient</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Integrated PostCreator */}
                    {((destination === 'profile') || 
                      (destination === 'community' && selectedCommunity) || 
                      (destination === 'dm' && selectedRecipient)) && (
                        <div className="bg-black/20 border border-white/5 rounded-[2rem] overflow-hidden p-2">
                            <PostCreator
                                onCreatePost={handlePostCreated}
                                postBlurOpacity={userOpacity * 100}
                                onCancel={onClose}
                                themeColor={userThemeColor}
                                isCommunityPost={destination === 'community'}
                                initialSharedContent={sharedData ? {
                                    text: sharedData.text || sharedData.url || '',
                                    media: (sharedData.files || []).map(f => ({
                                        url: f.url,
                                        type: f.type,
                                        name: f.url.split('/').pop() || 'file'
                                    }))
                                } : null}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
